import { Link } from 'react-router-dom';
import {
  formatDateShort,
  formatMonthHeading,
  isSameWeek,
  openSlots,
} from '@topspin/shared';
import type { Session } from '@topspin/shared';
import { today, type GroupView, type SeasonView } from '../data.js';
import { isInGroup, matchStatus, type MatchStatus } from '../session-utils.js';

export default function SeasonScreen({
  view,
  playerId,
}: {
  view: SeasonView;
  playerId: string;
}) {
  const date = today(view);
  const groups = view.groups.filter((g) => isInGroup(g, playerId));

  // Every session across both of Jeff's groups, in one chronological list.
  const rows = groups
    .flatMap((group) => group.sessions.map((session) => ({ group, session })))
    .sort((a, b) => a.session.date.localeCompare(b.session.date));

  const thisWeek = rows.filter((r) => isSameWeek(r.session.date, date));
  const rest = rows.filter((r) => !isSameWeek(r.session.date, date));

  const months = new Map<string, typeof rows>();
  for (const row of rest) {
    const heading = formatMonthHeading(row.session.date);
    months.set(heading, [...(months.get(heading) ?? []), row]);
  }

  return (
    <>
      <h1>Season {view.seasonId}</h1>

      {thisWeek.length > 0 && (
        <section className="card pinned">
          <h2>This week</h2>
          <ul className="season-list">
            {thisWeek.map(({ group, session }) => (
              <Row key={rowKey(group, session)} group={group} session={session} playerId={playerId} />
            ))}
          </ul>
        </section>
      )}

      {[...months].map(([heading, items]) => (
        <section className="card" key={heading}>
          <h2>{heading}</h2>
          <ul className="season-list">
            {items.map(({ group, session }) => (
              <Row key={rowKey(group, session)} group={group} session={session} playerId={playerId} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

const rowKey = (group: GroupView, session: Session) =>
  `${group.config.groupId}-${session.date}`;

function Row({
  group,
  session,
  playerId,
}: {
  group: GroupView;
  session: Session;
  playerId: string;
}) {
  // The same three states the match card colours itself by, from the same helper, so a night
  // can't be green here and red a tap away.
  const tone = matchStatus(group, session, playerId);
  const needed = openSlots(session, group.fills);

  return (
    <li>
      <Link
        className={tone ? `status-${tone}` : undefined}
        to={`/session/${group.config.groupId}/${session.date}`}
      >
        <span className="row-date">{formatDateShort(session.date)}</span>
        {/* No group name: every Tuesday date is the Tuesday group and every Thursday one the
            Thursday group, so naming it repeats what the date on the left already says. */}
        <span className="row-note">
          {/* "no tennis" rather than the note itself ("Thanksgiving", "Fourth of July"): the
              reason wraps this column onto a second line, and the date beside it already
              says which holiday it is. The note is spelled out on the session screen. */}
          {session.status === 'no-session' && 'no tennis'}
          {/* Its own word rather than a pill variant: the pill answers "am I playing", and
              that question still needs answering on a week the group is short. Short enough
              to sit on one line beside a date and a pill. */}
          {needed > 0 && <span className="needs-flag">{needed} short</span>}
        </span>
        <span className={`pill ${tone ?? 'off'}`}>{tone ? PILL[tone] : 'Off'}</span>
      </Link>
    </li>
  );
}

/**
 * The word beside the colour, one syllable each. Written in sentence case and uppercased in CSS,
 * because "OUT" spelled out in the markup is read letter by letter by some screen readers.
 *
 * "Off" covers a no-session date and, on a scheduled one, a player who isn't in that night's
 * session at all — the two cases with no status to report, and the two that get no colour.
 */
const PILL: Record<MatchStatus, string> = { in: 'In', sub: 'Sub', out: 'Out' };
